/* eslint-disable camelcase */
/** @jsxImportSource @emotion/react */
import { memo, useState } from "react";
import { useFormContext, useWatch, Controller } from "react-hook-form";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { Grid, TextField } from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";

import { Group, Label, Checkbox, RadioGroup } from "..";
import useHyperlinksByLocale from "../../hooks/useHyperlinksByLocale";
import { group } from "../../styles/layout";
import { ActivityMode, DisplayMode, VodType } from "../../types";
import { getDate } from "../../utils/converters";
import PreviewVideoDialog from "../PreviewVideoDialog";

import { disabled, previewVodButton, link, radioVodType, hidden } from "./styles";

export interface RecordingVodValues {
  enableVod: boolean;
  vodType: VodType;
  loomApiKey: string;
  loomJobId: string;
  catchupStartTime: any;
  vodStartTime: any;
  vodEndTime: any;
  vodChatroom: boolean;
  vodDisplayMode: DisplayMode;
}

interface Props {
  editable: boolean;
  chatroomEnabled: boolean;
  isLoomInfoEditable: boolean;
  isEventAfterPreview: boolean;
  catchupStartedAt?: number;
  vodStartedAt?: number;
  vodEndedAt?: number;
  isEventAfterEnded: boolean;
  isEventStartVodInFiveMinsOrAfterVod: boolean;
  isEventAfterLive: boolean;
  previewable: boolean;
  onVodChatroomChange: () => void;
}

const RecordingVodGroup = ({
  editable,
  chatroomEnabled,
  isLoomInfoEditable,
  isEventAfterPreview,
  catchupStartedAt,
  vodStartedAt,
  vodEndedAt,
  isEventAfterEnded,
  isEventAfterLive,
  isEventStartVodInFiveMinsOrAfterVod,
  previewable,
  onVodChatroomChange,
}: Props): JSX.Element => {
  const intl = useIntl();
  const { id } = useParams<{ id?: string }>();
  const { register, control, errors, getValues } = useFormContext();
  const {
    LOOM_ACCOUNT_REGISTRATION_URL,
    LOOM_BEFORE_YOU_START_URL,
    LOOM_CHECK_ENCODING_STATUAS_URL,
  } = useHyperlinksByLocale();

  const [openPreview, setOpenPreview] = useState(false);

  const { activityMode } = getValues(["activityMode"]);

  // If use optional rendering to register a field which didn't render at first time, the field can't be reset with data and would be defaultValue
  const {
    enableVod,
    vodType,
    loomApiKey,
    loomJobId,
    vodStartTime,
    vodChatroom,
    vodDisplayMode,
  } = useWatch<RecordingVodValues>({
    control,
    name: [
      "enableVod",
      "vodType",
      "loomApiKey",
      "loomJobId",
      "catchupStartTime",
      "vodStartTime",
      "vodChatroom",
      "vodDisplayMode",
    ],
    defaultValue: {
      enableVod: false,
      vodType: VodType.LiveEncoding,
      loomApiKey: "",
      loomJobId: "",
      catchupStartTime: getDate(catchupStartedAt),
      vodStartTime: getDate(vodStartedAt),
      vodChatroom: false,
      vodDisplayMode: DisplayMode.DarkMode,
    },
  });

  const isVodReplacement = vodType === VodType.VodReplacement;
  const isPlaybackMode = activityMode === ActivityMode.PlaybackMode;
  return (
    <>
      <Group
        css={group}
        title={intl.formatMessage({ id: "general.catch-up-settings" })}
        subtitle={intl.formatMessage(
          {
            id: "event-detail.recording-for-vod.description",
          },
          {
            a: (chunk) => (
              <a href={LOOM_ACCOUNT_REGISTRATION_URL} target="_blank" rel="noreferrer noopener" css={link}>
                {chunk}
              </a>
            ),
          }
        )}
        {...(!editable &&
          isVodReplacement && {
            actions: [
              {
                label: intl.formatMessage({
                  id: "vod-to-live-page.preview-vod",
                }),
                icon: "reveal",
                color: "white",
                style: previewVodButton,
                disabled: !previewable || isEventAfterEnded,
                onClick: () => setOpenPreview(true),
              },
            ],
          })}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Label
              text={intl.formatMessage({
                id: "event-detail.catch-up",
              })}
              tip={intl.formatMessage({
                id: "event-detail.catch-up.tip",
              })}
            />
            <Controller
              render={({ onChange, value, ...rest }): JSX.Element => (
                <Checkbox
                  label={intl.formatMessage({
                    id: "event-detail.enable-catch-up",
                  })}
                  checked={value}
                  onChange={(e): void => onChange(e.target.checked)}
                  disabled={!editable || isPlaybackMode || (!!id && isEventAfterPreview)}
                  {...rest}
                />
              )}
              name="enableVod"
              control={control}
              defaultValue={enableVod}
            />
          </Grid>

          {/* If use css to hide fields, need to check required fields and rules  */}
          <Grid container item spacing={3} css={!enableVod && hidden}>
            <Grid item xs={12} css={radioVodType}>
              <Controller
                render={({ onChange, ref, ...rest }): JSX.Element => (
                  <RadioGroup
                    radios={[
                      {
                        label: intl.formatMessage({
                          id: "event-detail.vod-type.event-recording",
                        }),
                        value: VodType.LiveEncoding,
                      },
                      {
                        label: intl.formatMessage({
                          id: "event-detail.vod-type.vod-replacement",
                        }),
                        value: VodType.VodReplacement,
                        iconTip: intl.formatMessage({
                          id: "event-detail.vod-type.vod-replacement.tip",
                        }),
                      },
                    ]}
                    onChange={(e): void => {
                      onChange(e.target.value);
                    }}
                    disabled={!editable || (!!id && !isLoomInfoEditable)}
                    {...rest}
                  />
                )}
                name="vodType"
                control={control}
                defaultValue={vodType}
              />
            </Grid>

            {isVodReplacement && (
              <Grid container item spacing={3}>
                <Grid item xs={6}>
                  <Label
                    text={intl.formatMessage({
                      id: "vod-to-live-page.loom-api-key",
                    })}
                    tip={intl.formatMessage(
                      {
                        id: "vod-to-live-page.loom-api-key.tooltip",
                      },
                      {
                        a: (chunk) => (
                          <a href={LOOM_BEFORE_YOU_START_URL} target="_blank" rel="noreferrer noopener" css={link}>
                            {chunk}
                          </a>
                        ),
                      }
                    )}
                  />
                  <TextField
                    css={disabled}
                    fullWidth
                    name="loomApiKey"
                    inputRef={register({
                      required: enableVod && isVodReplacement,
                    })}
                    inputProps={{
                      readOnly: !editable || (!!id && !isLoomInfoEditable),
                    }}
                    error={!!errors.loomApiKey}
                    helperText={
                      errors.loomApiKey &&
                      errors.loomApiKey.type === "required" &&
                      intl.formatMessage({ id: "validation.required" })
                    }
                    defaultValue={loomApiKey}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Label
                    text={intl.formatMessage({
                      id: "vod-to-live-page.loom-job-id",
                    })}
                    tip={intl.formatMessage(
                      {
                        id: "vod-to-live-page.loom-job-id.tooltip",
                      },
                      {
                        a: (chunk) => (
                          <a
                            href={LOOM_CHECK_ENCODING_STATUAS_URL}
                            target="_blank"
                            rel="noreferrer noopener"
                            css={link}
                          >
                            {chunk}
                          </a>
                        ),
                      }
                    )}
                  />
                  <TextField
                    css={disabled}
                    fullWidth
                    name="loomJobId"
                    inputRef={register({
                      required: enableVod && isVodReplacement,
                    })}
                    inputProps={{
                      readOnly: !editable || (!!id && !isLoomInfoEditable),
                    }}
                    error={!!errors.loomJobId}
                    helperText={
                      errors.loomJobId &&
                      errors.loomJobId.type === "required" &&
                      intl.formatMessage({ id: "validation.required" })
                    }
                    defaultValue={loomJobId}
                  />
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Label
                text={intl.formatMessage({
                  id: "event-detail.chatroom-poll",
                })}
                tip={intl.formatMessage({
                  id: "event-detail.chatroom-poll.tip",
                })}
              />
              <Controller
                render={({ value, onChange, ref, ...rest }): JSX.Element => (
                  <Checkbox
                    label={intl.formatMessage({
                      id: "event-detail.chatroom-poll.enable",
                    })}
                    checked={value}
                    onChange={(e): void => {
                      onVodChatroomChange();
                      onChange(e.target.checked);
                    }}
                    disabled={!!id || !chatroomEnabled}
                    {...rest}
                  />
                )}
                name="vodChatroom"
                control={control}
                defaultValue={vodChatroom}
              />
              <Grid item css={!vodChatroom && hidden}>
                <Controller
                  render={({ ref, onChange, ...rest }): JSX.Element => (
                    <RadioGroup
                      radios={[
                        {
                          label: intl.formatMessage({
                            id: "event-detail.display-mode.dark-mode",
                          }),
                          value: DisplayMode.DarkMode,
                        },
                        {
                          label: intl.formatMessage({
                            id: "event-detail.display-mode.light-mode",
                          }),
                          value: DisplayMode.LightMode,
                        },
                      ]}
                      disabled={!!id}
                      onChange={(e): void => {
                        onVodChatroomChange();
                        onChange(e.target.value);
                      }}
                      {...rest}
                    />
                  )}
                  defaultValue={vodDisplayMode}
                  name="vodDisplayMode"
                  control={control}
                />
              </Grid>
            </Grid>

            <Grid container item spacing={3} css={!enableVod && hidden}>
              <Grid item xs={6}>
                <Label
                  text={intl.formatMessage({
                    id: "event-detail.catch-up-start-time",
                  })}
                  tip={intl.formatMessage({
                    id: "event-detail.catch-up-start-time.tip",
                  })}
                />
                <Controller
                  render={({ ref, ...rest }): JSX.Element => (
                    <DateTimePicker
                      cancelLabel={intl.formatMessage({
                        id: "btn.cancel",
                      })}
                      format="MM/dd/yyyy HH:mm"
                      disablePast
                      minutesStep={5}
                      fullWidth
                      error={!!errors.catchupStartTime}
                      helperText={
                        errors.catchupStartTime &&
                        (errors.catchupStartTime.type === "required"
                          ? intl.formatMessage({ id: "validation.required" })
                          : "")
                      }
                      disabled={!editable || isEventAfterLive}
                      {...rest}
                    />
                  )}
                  name="catchupStartTime"
                  control={control}
                  rules={{
                    required: enableVod,
                  }}
                  defaultValue={getDate(catchupStartedAt)}
                />
              </Grid>
            </Grid>
            <Grid item xs={6}>
              <Label
                text={intl.formatMessage({
                  id: "event-detail.publish-start-time",
                })}
                tip={intl.formatMessage({
                  id: "event-detail.publish-start-time.tip",
                })}
              />
              <Controller
                render={({ ref, ...rest }): JSX.Element => (
                  <DateTimePicker
                    cancelLabel={intl.formatMessage({
                      id: "btn.cancel",
                    })}
                    format="MM/dd/yyyy HH:mm"
                    disablePast
                    minutesStep={5}
                    fullWidth
                    error={!!errors.vodStartTime}
                    helperText={
                      errors.vodStartTime &&
                      intl.formatMessage({
                        id: errors.vodStartTime.type === "required" ? "validation.required" : "validation.invalid-time",
                      })
                    }
                    disabled={!editable || isEventStartVodInFiveMinsOrAfterVod}
                    {...rest}
                  />
                )}
                name="vodStartTime"
                control={control}
                rules={{
                  required: enableVod,
                  validate:
                    enableVod && !isEventStartVodInFiveMinsOrAfterVod
                      ? (date): boolean => Date.parse(date) > Date.now()
                      : undefined,
                }}
                defaultValue={getDate(vodStartedAt)}
              />
            </Grid>
            <Grid item xs={6}>
              <Label
                text={intl.formatMessage({
                  id: "event-detail.publish-end-time",
                })}
                tip={intl.formatMessage({
                  id: "event-detail.publish-end-time.tip",
                })}
              />
              <Controller
                render={({ ref, ...rest }): JSX.Element => (
                  <DateTimePicker
                    cancelLabel={intl.formatMessage({
                      id: "btn.cancel",
                    })}
                    format="MM/dd/yyyy HH:mm"
                    disablePast
                    minDate={vodStartTime}
                    minutesStep={5}
                    fullWidth
                    error={!!errors.vodEndTime}
                    helperText={
                      errors.vodEndTime &&
                      intl.formatMessage({
                        id: errors.vodEndTime.type === "required" ? "validation.required" : "validation.invalid-time",
                      })
                    }
                    disabled={!editable || !vodStartTime || isEventAfterEnded}
                    {...rest}
                  />
                )}
                name="vodEndTime"
                control={control}
                rules={{
                  required: enableVod,
                  validate:
                    enableVod && !isEventAfterEnded
                      ? (date): boolean => Date.parse(date) > Date.parse(vodStartTime)
                      : undefined,
                }}
                defaultValue={getDate(vodEndedAt)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Group>
      <PreviewVideoDialog
        src=""
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        onBackButtonClick={() => setOpenPreview(false)}
        native={false}
      />
    </>
  );
};

export default memo(RecordingVodGroup);
